import React, { useState, useMemo, useEffect } from 'react'
import {
  Container,
  Header,
  Button,
  ButtonDropdown,
  DateRangePicker,
  SpaceBetween,
  Box,
  Badge,
  Icon,
  FormField
} from '@cloudscape-design/components'
import { 
  useDashboardContext, 
  DEFAULT_TIME_RANGES, 
  DEFAULT_REFRESH_INTERVALS,
  getTimeRangeDates,
  type TimeRange 
} from '../contexts/DashboardContext'

interface DashboardControlsProps {
  showFilters?: boolean
}

export const DashboardControls: React.FC<DashboardControlsProps> = ({
  showFilters = true
}) => {
  const {
    timeRange,
    setTimeRange,
    refreshInterval,
    setRefreshInterval,
    triggerRefresh,
    filters,
    removeFilter,
    isRefreshing
  } = useDashboardContext()

  const [currentMinute, setCurrentMinute] = useState(() => {
    const now = new Date()
    return new Date(now.getFullYear(), now.getMonth(), now.getDate(), now.getHours(), now.getMinutes())
  })

  // Update current minute only once per minute for relative time ranges
  useEffect(() => {
    if (timeRange.type !== 'relative') return

    const now = new Date()
    const msUntilNextMinute = (60 - now.getSeconds()) * 1000 - now.getMilliseconds()

    const timeoutId = setTimeout(() => {
      const newMinute = new Date()
      newMinute.setSeconds(0, 0)
      setCurrentMinute(newMinute)

      // Set interval for subsequent minutes
      const intervalId = setInterval(() => {
        const nextMinute = new Date()
        nextMinute.setSeconds(0, 0)
        setCurrentMinute(nextMinute)
      }, 60000)

      return () => clearInterval(intervalId)
    }, msUntilNextMinute)

    return () => clearTimeout(timeoutId)
  }, [timeRange.type])

  // Convert current time range for display - stable for relative ranges
  const currentDates = useMemo(() => {
    // For absolute ranges, use the exact dates
    if (timeRange.type === 'absolute' && timeRange.absolute) {
      return {
        start: new Date(timeRange.absolute.startDate),
        end: new Date(timeRange.absolute.endDate)
      }
    }
    
    // For relative ranges, use the rounded current minute
    if (timeRange.type === 'relative' && timeRange.relative) {
      let startTime = currentMinute.getTime()
      const { amount, unit } = timeRange.relative
      
      switch (unit) {
        case 'minute':
          startTime -= amount * 60 * 1000
          break
        case 'hour':
          startTime -= amount * 60 * 60 * 1000
          break
        case 'day':
          startTime -= amount * 24 * 60 * 60 * 1000
          break
        case 'week':
          startTime -= amount * 7 * 24 * 60 * 60 * 1000
          break
        case 'month':
          startTime -= amount * 30 * 24 * 60 * 60 * 1000
          break
      }
      
      return {
        start: new Date(startTime),
        end: new Date(currentMinute)
      }
    }
    
    // Fallback
    return getTimeRangeDates(timeRange)
  }, [timeRange, currentMinute])

  // Refresh interval dropdown items
  const refreshItems = DEFAULT_REFRESH_INTERVALS.map(interval => ({
    id: interval.value.toString(),
    text: interval.label,
    data: interval.value
  }))

  const refreshIntervalLabel = DEFAULT_REFRESH_INTERVALS.find(
    interval => interval.value === refreshInterval
  )?.label || `${refreshInterval}s`

  const handleRefreshIntervalSelect = ({ detail }: any) => {
    const selectedItem = refreshItems.find(item => item.id === detail.id)
    if (selectedItem) {
      setRefreshInterval(selectedItem.data)
    }
  }

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    }}>
      {/* Time Controls */}
          {/* Date Range Picker with Icon */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Icon name="calendar" size="small" />
            <DateRangePicker
              value={{
                type: 'absolute',
                startDate: currentDates.start.toISOString(),
                endDate: currentDates.end.toISOString()
              }}
              onChange={({ detail }) => {
                if (detail.value?.type === 'absolute') {
                  // Validate time range before setting
                  const now = new Date()
                  const startDate = new Date(detail.value.startDate)
                  let endDate = new Date(detail.value.endDate)
                  
                  // Clamp end date to current time if it's in the future
                  if (endDate > now) {
                    console.log(`📅 Clamping future end date ${endDate.toISOString()} to now: ${now.toISOString()}`)
                    endDate = now
                  }
                  
                  // Ensure start is before end
                  if (startDate >= endDate) {
                    console.log(`📅 Start date ${startDate.toISOString()} is after end date, adjusting`)
                    startDate.setTime(endDate.getTime() - 60 * 60 * 1000) // Set start to 1 hour before end
                  }
                  
                  const customTimeRange: TimeRange = {
                    type: 'absolute',
                    absolute: {
                      startDate: startDate.toISOString(),
                      endDate: endDate.toISOString()
                    }
                  }
                  setTimeRange(customTimeRange)
                } else if (detail.value?.type === 'relative' && detail.value.amount && detail.value.unit) {
                  // Find matching relative option
                  const relativeOption = DEFAULT_TIME_RANGES.find(range => 
                    range.relative?.amount === detail.value.amount && 
                    range.relative?.unit === detail.value.unit
                  )
                  if (relativeOption) {
                    setTimeRange(relativeOption)
                  }
                }
              }}
              relativeOptions={[
                {
                  key: 'previous-5-minutes',
                  amount: 5,
                  unit: 'minute',
                  type: 'relative'
                },
                {
                  key: 'previous-30-minutes',
                  amount: 30,
                  unit: 'minute',
                  type: 'relative'
                },
                {
                  key: 'previous-1-hour',
                  amount: 1,
                  unit: 'hour',
                  type: 'relative'
                },
                {
                  key: 'previous-6-hours',
                  amount: 6,
                  unit: 'hour',
                  type: 'relative'
                },
                {
                  key: 'previous-24-hours',
                  amount: 24,
                  unit: 'hour',
                  type: 'relative'
                },
                {
                  key: 'previous-7-days',
                  amount: 7,
                  unit: 'day',
                  type: 'relative'
                },
                {
                  key: 'previous-30-days',
                  amount: 30,
                  unit: 'day',
                  type: 'relative'
                }
              ]}
              isValidRange={(range: any) => {
                if (range?.type === 'absolute') {
                  const [startDateWithoutTime] = range.startDate.split('T')
                  const [endDateWithoutTime] = range.endDate.split('T')
                  if (!startDateWithoutTime || !endDateWithoutTime) {
                    return {
                      valid: false,
                      errorMessage: 'The selected date range is incomplete. Select a start and end date for the date range.'
                    }
                  }
                  if (new Date(range.startDate) - new Date(range.endDate) > 0) {
                    return {
                      valid: false,
                      errorMessage: 'The selected date range is invalid. The start date must be before the end date.'
                    }
                  }
                }
                return { valid: true }
              }}
              absoluteFormat="short-localized"
              placeholder="Select time range"
              i18nStrings={{
                todayAriaLabel: "Today",
                nextMonthAriaLabel: "Next month", 
                previousMonthAriaLabel: "Previous month",
                customRelativeRangeDurationLabel: "Duration",
                customRelativeRangeDurationPlaceholder: "Enter duration",
                customRelativeRangeOptionLabel: "Custom range",
                customRelativeRangeOptionDescription: "Set a custom range in the past",
                customRelativeRangeUnitLabel: "Unit of time",
                formatRelativeRange: (e) => {
                  const n = 1 === e.amount ? "" : "s";
                  return `Last ${e.amount} ${e.unit}${n}`;
                },
                formatUnit: (e, n) => (1 === n ? e : `${e}s`),
                dateTimeConstraintText: "Range is 6 to 30 characters. Use format yyyy/mm/dd hh:mm:ss.",
                relativeModeTitle: "Relative range",
                absoluteModeTitle: "Absolute range", 
                relativeRangeSelectionHeading: "Choose a range",
                startDateLabel: "Start date",
                endDateLabel: "End date",
                startTimeLabel: "Start time",
                endTimeLabel: "End time",
                clearButtonLabel: "Clear and dismiss",
                cancelButtonLabel: "Cancel",
                applyButtonLabel: "Apply"
              }}
            />
          </div>

          {/* Refresh Interval */}
          <ButtonDropdown
            items={refreshItems}
            onItemClick={handleRefreshIntervalSelect}
            variant="icon"
            iconName="settings"
            ariaLabel={`Auto-refresh: ${refreshIntervalLabel}`}
          />

          {/* Manual Refresh */}
          <Button
            iconName="refresh"
            onClick={triggerRefresh}
            loading={isRefreshing}
            variant="icon"
            ariaLabel="Refresh dashboard"
          />

          {/* Active Filters */}
          {showFilters && filters.length > 0 && (
            <>
              {filters.map(filter => (
                <Badge
                  key={filter.id}
                  color="blue"
                  onDismiss={() => removeFilter(filter.id)}
                >
                  {filter.label}: {filter.values.join(', ')}
                </Badge>
              ))}
            </>
          )}
    </div>
  )
}